import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../../services/api';
import { format } from 'date-fns';

const UserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('profile');

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoading(true);
                const response = await userService.getUser(id);
                setUser(response.data);
            } catch (error) {
                console.error("Error fetching user details:", error);
                // Alert or toast notification could go here
            } finally {
                setLoading(false);
            }
        };

        loadUser();
    }, [id]);

    const handleStatusChange = async (status) => {
        if (window.confirm(`Are you sure you want to ${status ? 'activate' : 'deactivate'} this user?`)) {
            try {
                await userService.updateUser(id, { isActive: status });
                setUser(prev => ({ ...prev, isActive: status }));
                // Success notification
            } catch (error) {
                console.error("Error updating user status:", error);
                // Error notification
            }
        }
    };

    const handleResetPassword = async () => {
        if (window.confirm("Are you sure you want to reset this user's password?")) {
            try {
                await userService.resetPassword(id);
                // Success notification
                alert("Password reset email has been sent to the user.");
            } catch (error) {
                console.error("Error resetting password:", error);
                // Error notification
            }
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">User Not Found</h2>
                    <p className="text-gray-600 mb-6">The user you're looking for does not exist or has been removed.</p>
                    <button
                        onClick={() => navigate('/users')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
                    >
                        Return to Users List
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-wrap justify-between items-center">
                    <div>
                        <div className="flex items-center">
                            <button
                                onClick={() => navigate('/users')}
                                className="mr-4 text-gray-500 hover:text-gray-700"
                            >
                                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                            </button>
                            <h1 className="text-2xl font-bold text-gray-800">{user.name}</h1>
                        </div>
                        <p className="mt-1 text-gray-600">{user.email}</p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <span className={`px-2 py-1 text-xs rounded-full ${
                user.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {user.is_active ? 'Active' : 'Inactive'}
            </span>
                        <button
                            dataUid={user.id}
                            onClick={() => navigate(`/users/edit/${user.id}`)}
                            className="px-3 py-1 bg-white border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 focus:outline-none"
                        >
                            Edit
                        </button>
                        <div className="relative inline-block text-left">
                            <button
                                type="button"
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 focus:outline-none"
                                onClick={() => {
                                    // Show dropdown or menu of actions
                                }}
                            >
                                Actions
                            </button>
                            {/* Actions dropdown would go here */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'profile'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('profile')}
                    >
                        Profile
                    </button>
                    <button
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'activity'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('activity')}
                    >
                        Activity Log
                    </button>
                    <button
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'security'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('security')}
                    >
                        Security
                    </button>
                </nav>
            </div>

            {/* Content based on active tab */}
            <div className="bg-white rounded-lg shadow">
                {activeTab === 'profile' && (
                    <div className="p-6">
                        <div className="flex flex-col md:flex-row">
                            <div className="md:w-1/3 mb-6 md:mb-0 md:pr-6">
                                <div className="flex justify-center">
                                    <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-4xl">
                                        {user.name.charAt(0).toUpperCase()}
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <span className="block text-sm font-medium text-gray-500">Full Name</span>
                                            <span className="block mt-1 text-sm text-gray-900">{user.name}</span>
                                        </div>
                                        <div>
                                            <span className="block text-sm font-medium text-gray-500">Email</span>
                                            <span className="block mt-1 text-sm text-gray-900">{user.email}</span>
                                        </div>
                                        {user.phone && (
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">Phone</span>
                                                <span className="block mt-1 text-sm text-gray-900">{user.phone}</span>
                                            </div>
                                        )}
                                        <div>
                                            <span className="block text-sm font-medium text-gray-500">Roles</span>
                                            <div className="mt-1">
                                                {user.roles && user.roles.map(role => (
                                                    <span
                                                        key={role.id}
                                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2 mb-2"
                                                    >
                            {role.name}
                          </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="block text-sm font-medium text-gray-500">Status</span>
                                            <span className={`block mt-1 text-sm ${user.is_active ? 'text-green-600' : 'text-red-600'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="md:w-2/3 md:pl-6 md:border-l md:border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <span className="block text-sm font-medium text-gray-500">Username</span>
                                        <span className="block mt-1 text-sm text-gray-900">{user.username || 'N/A'}</span>
                                    </div>
                                    <div>
                                        <span className="block text-sm font-medium text-gray-500">Created On</span>
                                        <span className="block mt-1 text-sm text-gray-900">
                      {user.created_at ? format(new Date(user.created_at), 'MMMM d, yyyy') : 'N/A'}
                    </span>
                                    </div>
                                    <div>
                                        <span className="block text-sm font-medium text-gray-500">Last Login</span>
                                        <span className="block mt-1 text-sm text-gray-900">
                      {user.last_login ? format(new Date(user.last_login), 'MMMM d, yyyy h:mm a') : 'Never'}
                    </span>
                                    </div>
                                    <div>
                                        <span className="block text-sm font-medium text-gray-500">Last Updated</span>
                                        <span className="block mt-1 text-sm text-gray-900">
                      {user.updated_at ? format(new Date(user.updated_at), 'MMMM d, yyyy') : 'N/A'}
                    </span>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <div className="flex items-center h-5">
                                                <input
                                                    id="email_notifications"
                                                    type="checkbox"
                                                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                                    checked={user.notification_preferences?.email}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="ml-3 text-sm">
                                                <label htmlFor="email_notifications" className="font-medium text-gray-700">Email Notifications</label>
                                                <p className="text-gray-500">Receive updates and alerts via email.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="flex items-center h-5">
                                                <input
                                                    id="sms_notifications"
                                                    type="checkbox"
                                                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                                    checked={user.notification_preferences?.sms}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="ml-3 text-sm">
                                                <label htmlFor="sms_notifications" className="font-medium text-gray-700">SMS Notifications</label>
                                                <p className="text-gray-500">Receive important alerts via text message.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
                                    <div className="flex flex-wrap gap-3">
                                        {user.is_active ? (
                                            <button
                                                onClick={() => handleStatusChange(false)}
                                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                            >
                                                Deactivate Account
                                            </button>
                                        ) : (
                                             <button
                                                 onClick={() => handleStatusChange(true)}
                                                 className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                             >
                                                 Activate Account
                                             </button>
                                         )}
                                        <button
                                            onClick={handleResetPassword}
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            Reset Password
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'activity' && (
                    <div className="p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Activity Log</h3>

                        {user.activityLog && user.activityLog.length > 0 ? (
                            <div className="flow-root">
                                <ul className="-mb-8">
                                    {user.activityLog.map((activity, index) => (
                                        <li key={activity.id}>
                                            <div className="relative pb-8">
                                                {index !== user.activityLog.length - 1 ? (
                                                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                                                ) : null}
                                                <div className="relative flex space-x-3">
                                                    <div>
                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                                activity.type === 'login' ? 'bg-green-500' :
                                activity.type === 'logout' ? 'bg-yellow-500' :
                                activity.type === 'update' ? 'bg-blue-500' :
                                activity.type === 'create' ? 'bg-purple-500' :
                                'bg-gray-500'
                            }`}>
                              <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                {activity.type === 'login' && (
                                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                                )}
                                  {activity.type === 'logout' && (
                                      <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm10.293 9.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 111.414 1.414L11.414 9H14a1 1 0 110 2h-2.586l1.293 1.293z" clipRule="evenodd" />
                                  )}
                                  {activity.type === 'update' && (
                                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  )}
                                  {activity.type === 'create' && (
                                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                  )}
                                  {(activity.type !== 'login' && activity.type !== 'logout' && activity.type !== 'update' && activity.type !== 'create') && (
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  )}
                              </svg>
                            </span>
                                                    </div>
                                                    <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                                        <div>
                                                            <p className="text-sm text-gray-500">{activity.description}</p>
                                                        </div>
                                                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                                            {format(new Date(activity.timestamp), 'MMM d, yyyy h:mm a')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                             <div className="text-center py-8 text-gray-500">
                                 No activity logged for this user
                             </div>
                         )}
                    </div>
                )}

                {activeTab === 'security' && (
                    <div className="p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>

                        <div className="space-y-6">
                            <div>
                                <h4 className="text-base font-medium text-gray-900">Password Management</h4>
                                <p className="mt-1 text-sm text-gray-500">
                                    Reset the user's password by sending them a password reset link via email.
                                </p>
                                <button
                                    onClick={handleResetPassword}
                                    className="mt-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Send Password Reset Link
                                </button>
                            </div>

                            <div className="pt-6 border-t border-gray-200">
                                <h4 className="text-base font-medium text-gray-900">Two-Factor Authentication</h4>
                                <p className="mt-1 text-sm text-gray-500">
                                    {user.twoFactorEnabled
                                     ? 'Two-factor authentication is currently enabled for this user.'
                                     : 'Two-factor authentication is currently disabled for this user.'}
                                </p>
                                <div className="mt-3">
                                    {user.twoFactorEnabled ? (
                                        <button
                                            onClick={() => {
                                                // Would handle disabling 2FA
                                            }}
                                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                        >
                                            Disable Two-Factor Authentication
                                        </button>
                                    ) : (
                                         <span className="text-sm text-gray-500">
                      The user can enable two-factor authentication from their profile settings.
                    </span>
                                     )}
                                </div>
                            </div>

                            <div className="pt-6 border-t border-gray-200">
                                <h4 className="text-base font-medium text-gray-900">Recent Login Sessions</h4>
                                {user.loginSessions && user.loginSessions.length > 0 ? (
                                    <div className="mt-3 overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device / Browser</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                            {user.loginSessions.map((session) => (
                                                <tr key={session.id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.ipAddress}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.userAgent}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.location || 'Unknown'}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {format(new Date(session.timestamp), 'MMM d, yyyy h:mm a')}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {session.active ? (
                                                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                  Active
                                </span>
                                                        ) : (
                                                             <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                  Ended
                                </span>
                                                         )}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                     <div className="mt-3 text-sm text-gray-500">
                                         No login sessions recorded for this user.
                                     </div>
                                 )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserDetails;
